import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';

interface UseUrlSearchOptions {
    debounceTimeout?: number;
    onSearchChange?: (value: string) => void;
}

interface UseUrlSearchResult {
    search: string;
    setSearch: (value: string) => void;
    clearSearch: () => void;
}

/**
 * Hook for preserving search query params in the URL.
 * Resets page parameter when search changes.
 */
const useUrlSearch = ({ debounceTimeout = 500, onSearchChange }: UseUrlSearchOptions = {}): UseUrlSearchResult => {
    const searchParamName = 'search';
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearchState] = useState<string>(() => searchParams.get(searchParamName) || '');

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInternalUpdateRef = useRef(false);
    const searchRef = useRef<string>('');
    const onSearchChangeRef = useRef(onSearchChange);

    onSearchChangeRef.current = onSearchChange;
    searchRef.current = search;

    // Sync local state when URL params change externally (browser navigation)
    useEffect(() => {
        if (isInternalUpdateRef.current) {
            isInternalUpdateRef.current = false;
            return;
        }

        const urlValue = searchParams.get(searchParamName) || '';

        if (urlValue !== searchRef.current) {
            setSearchState(urlValue);
        }
    }, [searchParams]);

    // Update the URL with the new search value
    const updateUrl = useCallback(
        (value: string) => {
            isInternalUpdateRef.current = true;
            timeoutRef.current = null;

            setSearchParams(
                (prev: URLSearchParams) => {
                    const newParams = new URLSearchParams(prev);

                    if (value) {
                        newParams.set(searchParamName, value);
                    } else {
                        newParams.delete(searchParamName);
                    }

                    // Reset to first page when search changes
                    newParams.delete('page');

                    return newParams;
                },
                { replace: true }
            );

            onSearchChangeRef.current?.(value);
        },
        [setSearchParams]
    );

    // Set the search value and update the URL with debounce
    const setSearch = useCallback(
        (value: string) => {
            setSearchState(value);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                updateUrl(value);
            }, debounceTimeout);
        },
        [debounceTimeout, updateUrl]
    );

    // Clear the search value and update the URL
    const clearSearch = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = null;

        setSearchState('');
        updateUrl('');
    }, [updateUrl]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { search, setSearch, clearSearch };
};

export default useUrlSearch;
